
import React, { useState, useMemo, useEffect } from 'react';
import ProfileComponent from '../../components/Profile/ProfileComponent';
import { User, Language, IntegrationFlow, AgentProfile, IntegrationApp, ServiceDefinition, AccessToken, SubscriptionRecord, UserStatistics } from '../../types';
import { TRANSLATIONS, SERVICES_DATA } from '../../constants';
import { BillingService, AccessService, UsageService } from '../../api'; // UsageService import
import { useDataTemplate } from '../../hooks/useDataTemplate';

interface ProfileTemplateProps {
  user: User;
  language: Language;
  usageCount: number;
  flows: IntegrationFlow[];
  agents: AgentProfile[];
  onManageBilling: () => void;
  onBack: () => void;
  setAgents: React.Dispatch<React.SetStateAction<AgentProfile[]>>;
  setFlows: React.Dispatch<React.SetStateAction<IntegrationFlow[]>>;
  onUpdateUser: (user: Partial<User>) => void;
}

const ProfileTemplate: React.FC<ProfileTemplateProps> = (props) => {
  const { user, language, flows, agents, setAgents, setFlows, onUpdateUser } = props;
  const t = TRANSLATIONS[language];
  const [activeTab, setActiveTab] = useState<'overview' | 'services' | 'flows' | 'agents' | 'integrations' | 'keys' | 'subscriptions'>('overview');
  const [showSubSettings, setShowSubSettings] = useState(false);
  const [cancelStep, setCancelStep] = useState(0);
  
  // Fetch real data
  const { data: tokens, setData: setTokens } = useDataTemplate<AccessToken[]>(AccessService.getTokens);
  const { data: subscriptions, setData: setSubscriptions } = useDataTemplate<SubscriptionRecord[]>(BillingService.getSubscriptions);
  
  // NEW: Fetch Real Stats
  const { data: realStats } = useDataTemplate<UserStatistics>(UsageService.getStats);

  // Persistence
  useEffect(() => {
    if (tokens) localStorage.setItem('lahja_tokens', JSON.stringify(tokens));
  }, [tokens]);

  useEffect(() => {
    if (subscriptions) localStorage.setItem('lahja_subscriptions', JSON.stringify(subscriptions));
  }, [subscriptions]);

  const integrationApps: IntegrationApp[] = useMemo(() => {
     try {
         const saved = localStorage.getItem('lahja_integrations');
         // Filter to only show CONNECTED integrations in profile
         return saved ? JSON.parse(saved).filter((app: IntegrationApp) => app.connected) : [];
     } catch(e) { return []; }
  }, []);

  // Fallback if stats fail
  const stats = realStats || {
      totalRequests: 0,
      totalTimeSaved: 0,
      activeServices: 0,
      efficiencyScore: 0,
      usageByService: [],
      activityHistory: [0,0,0,0,0,0,0],
      usedServiceIds: []
  };

  // Filter Services based on actual usage
  const usedServicesData = useMemo(() => {
      const usedIds = new Set(stats.usedServiceIds || []);
      return SERVICES_DATA.filter(s => usedIds.has(s.id));
  }, [stats.usedServiceIds]);

  const [activeServices, setActiveServices] = useState<Set<string>>(new Set());

  // Update active services set when stats load
  useEffect(() => {
      if (stats.usedServiceIds) {
          setActiveServices(new Set(stats.usedServiceIds));
      }
  }, [stats.usedServiceIds]);

  const totalIntegrationTimeSaved = integrationApps.reduce((acc, app) => acc + (app.stats?.timeSaved || 0), 0);
  const totalIntegrationRequests = integrationApps.reduce((acc, app) => acc + (app.stats?.requests || 0), 0);

  const planDetails = {
    free: { color: 'slate', label: t.planFree, bg: 'from-slate-700 to-slate-900', icon: 'fa-seedling' },
    pro: { color: 'emerald', label: t.planPro, bg: 'from-emerald-500 to-teal-700', icon: 'fa-rocket' },
    enterprise: { color: 'blue', label: t.planBusiness, bg: 'from-blue-600 to-indigo-800', icon: 'fa-building-columns' }
  };
  const currentPlan = planDetails[user.plan || 'free'];

  // ... (Toggle Handlers remain same) ...
  const handleToggleService = (id: string) => {
    // In profile, "toggle" might essentially hide it from view if we are strictly showing usage
    // For now, let's just update the local Set to simulate on/off state in the UI list
    setActiveServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      return newSet;
    });
  };

  const handleToggleFlow = (id: string) => {
    setFlows(prev => prev.map(f => f.id === id ? { ...f, active: !f.active } : f));
  };

  const handleToggleAgent = (id: string) => {
    setAgents(prev => prev.map(a => a.id === id ? { ...a, autoReply: !a.autoReply } : a));
  };

  const handleCancelSubscription = () => {
    onUpdateUser({ plan: 'free' });
    setShowSubSettings(false);
    setCancelStep(0);
  };

  const handleCreateToken = async (name: string, type: AccessToken['type'], durationDays: number) => {
    try {
        const newToken = await AccessService.createToken(name, type, durationDays);
        setTokens(prev => [newToken, ...(prev || [])]);
    } catch (e) {
        console.error("Failed to create token", e);
    }
  };

  const handleRevokeToken = async (id: string) => {
    if (window.confirm('Revoke this key?')) {
      await AccessService.revokeToken(id);
      setTokens(prev => (prev || []).map(t => t.id === id ? { ...t, status: 'revoked' } : t));
    }
  };

  const handleSubscriptionAction = async (id: string, action: 'cancel' | 'renew' | 'toggle_renew' | 'pause' | 'resume') => {
    setSubscriptions(prev => (prev || []).map(sub => {
        if (sub.id !== id) return sub;
        if (action === 'cancel') {
            if (window.confirm('Are you sure?')) { BillingService.cancelSubscription(id); return { ...sub, status: 'canceled', autoRenew: false }; }
        }
        if (action === 'pause') { BillingService.pauseSubscription(id); return { ...sub, status: 'paused' }; }
        if (action === 'resume') { BillingService.resumeSubscription(id); return { ...sub, status: 'active' }; }
        if (action === 'renew') { BillingService.renewSubscription(id); return { ...sub, status: 'active', endDate: (sub.endDate || Date.now()) + 1000*60*60*24*30 }; }
        return sub;
    }));
  };

  return (
    <ProfileComponent
      user={user}
      language={language}
      activeTab={activeTab}
      currentPlan={currentPlan}
      showSubSettings={showSubSettings}
      cancelStep={cancelStep}
      servicesData={usedServicesData} // Only pass used services
      flows={flows} // Flows are already filtered by user ID context (conceptually) or local DB
      agents={agents}
      integrationApps={integrationApps} // Only connected apps
      activeServices={activeServices}
      // Pass real stats
      totalIntegrationRequests={stats.totalRequests} 
      totalIntegrationTimeSaved={stats.totalTimeSaved}
      tokens={tokens || []} 
      subscriptions={subscriptions || []} 
      onCreateToken={handleCreateToken}
      onRevokeToken={handleRevokeToken}
      onSubscriptionAction={handleSubscriptionAction}
      onBack={props.onBack}
      onSetActiveTab={setActiveTab}
      onManageBilling={() => setShowSubSettings(true)}
      onCloseSubSettings={() => setShowSubSettings(false)}
      onSetCancelStep={setCancelStep}
      onCancelSubscription={handleCancelSubscription}
      onToggleService={handleToggleService}
      onToggleFlow={handleToggleFlow}
      onToggleAgent={handleToggleAgent}
      statistics={stats}
    />
  );
};

export default ProfileTemplate;
